import { useEffect, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';

export const useKeyboardShortcuts = () => {
  const { 
    undo, redo, canUndo, canRedo,
    clearCanvas, removeAllImages,
    setSelectedImage, uploadedImages, selectedImageId,
    openDeleteConfirmation
  } = useApp();

  const handleKeyDown = useCallback((event) => {
    // Verificar se é uma combinação de teclas
    const isCtrlOrCmd = event.ctrlKey || event.metaKey;

    if (isCtrlOrCmd) {
      switch (event.key.toLowerCase()) {
        case 'z':
          event.preventDefault();

          // 🎨 PRIORIDADE: Verificar se está em modo Pintura2
          if (window.__painting2UndoHandler && window.__painting2CanUndo?.()) {
            if (event.shiftKey) {
              // Ctrl+Shift+Z = Redo no Croquis
              window.__painting2RedoHandler?.();
            } else {
              // Ctrl+Z = Undo no Croquis
              window.__painting2UndoHandler?.();
            }
          } else {
            // Undo/Redo global (quando não está em Pintura2)
            if (event.shiftKey) {
              if (canRedo) {
                redo();
              }
            } else {
              if (canUndo) {
                undo();
              }
            }
          }
          break;
        case 'y':
          event.preventDefault();

          // 🎨 PRIORIDADE: Verificar se está em modo Pintura2
          if (window.__painting2RedoHandler && window.__painting2CanRedo?.()) {
            window.__painting2RedoHandler?.();
          } else {
            // Ctrl+Y ou Cmd+Y = Redo global
            if (canRedo) {
              redo();
            }
          }
          break;
        case 'a':
          event.preventDefault();
          // Ctrl+A ou Cmd+A = Selecionar todas as imagens (se implementado)
          // Por enquanto, apenas selecionar a primeira imagem
          if (uploadedImages.length > 0) {
            setSelectedImage(uploadedImages[uploadedImages.length - 1].id);
          }
          break;
        case 'd':
          event.preventDefault();
          // Ctrl+D ou Cmd+D = Deselecionar todas as imagens
          setSelectedImage(null);
          break;
        case 'delete':
        case 'backspace':
          event.preventDefault();
          // Ctrl+Delete ou Cmd+Delete = Remover todas as imagens
          if (uploadedImages.length > 0) {
            removeAllImages();
          }
          break;
        default:
          break;
      }
    }
    
    // Atalhos sem modificadores
    switch (event.key.toLowerCase()) {
      case 'escape':
        event.preventDefault();
        // ESC = Deselecionar imagem atual
        setSelectedImage(null);
        break;
      case 'delete':
        event.preventDefault();
        // Delete = Excluir imagem selecionada com confirmação
        if (selectedImageId) {
          openDeleteConfirmation(selectedImageId);
        }
        break;
      default:
        break;
    }
  }, [undo, redo, canUndo, canRedo, clearCanvas, removeAllImages, setSelectedImage, uploadedImages, selectedImageId, openDeleteConfirmation]);

  useEffect(() => {
    // Adicionar listener de teclado
    document.addEventListener('keydown', handleKeyDown);
    
    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Retornar informações sobre os atalhos disponíveis
  return {
    shortcuts: {
      'Ctrl+Z / Cmd+Z': 'Desfazer (Undo)',
      'Ctrl+Y / Cmd+Y': 'Refazer (Redo)',
      'Ctrl+Shift+Z / Cmd+Shift+Z': 'Refazer (Redo)',
      'Ctrl+A / Cmd+A': 'Selecionar última imagem',
      'Ctrl+D / Cmd+D': 'Deselecionar todas as imagens',
      'Ctrl+Delete / Cmd+Delete': 'Remover todas as imagens',
      'ESC': 'Deselecionar imagem atual',
      'Delete': 'Excluir imagem selecionada',
    },
    canUndo,
    canRedo,
  };
};
